import { OPENING_AREA_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/openingAreaBackdrops';
import { sunkenBiome } from '../biomeArchetypes';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';

/** Tomb Waking — "The Waking Vault". The candle-lit wet ancient tomb where a
 *  fresh character wakes (On-Ramp Restructure, map 1 of 3). Evolved from
 *  `sunken_drift`: same dark-interior DNA (localized caustics, ONE dim drifting
 *  leviathan, cold spirit motes, column parallax, heavy dark grade) but shifted
 *  from teal cathedral to warm-candle sepulcher.
 *
 *  The signature is the candle `flicker` layer (via `extraLayers`) — warm
 *  orange flames clustered around the waking slab in the central chamber,
 *  against cold grey stone. The glow bed under the candles breathes with them
 *  (periodMs 800). Caustics sit only in the flooded alcove (bandY 0.85–1.0).
 *  One far-plane whale at alpha 0.06 is a "what was that?" beat, not a showcase.
 *  Interior opt-out (no celestial, no day-clock). sceneMood anchor 0.3. */
export const TOMB_WAKING = sunkenBiome({
  id: 'tomb_waking',
  sky: { from: 0x0a0a0c, to: 0x1a1a20 },
  motes: { colors: [0x8899aa, 0x6a7a8a], count: 12, sizeMin: 2, sizeMax: 4, driftPx: 14, periodMinMs: 14000, periodMaxMs: 26000, alphaMin: 0.1, alphaMax: 0.25 },
  glow: {
    blobs: [
      { color: 0x664422, x: 5200, y: 920, size: 320, alpha: 0.09 },
      { color: 0x5a3a1e, x: 5100, y: 940, size: 240, alpha: 0.06 },
      { color: 0x704a26, x: 5300, y: 940, size: 260, alpha: 0.07 },
    ],
    breathe: { periodMs: 800, min: 0.6, max: 1.2 },
  },
  lightField: { palette: [0x334455, 0x223344], count: 5, sizeMin: 80, sizeMax: 150, alphaMin: 0.02, alphaMax: 0.06, driftRadius: 40, periodMinMs: 15000, periodMaxMs: 25000 },
  caustics: { color: 0x224444, count: 3, sizeMin: 100, sizeMax: 220, alphaMin: 0.04, alphaMax: 0.12, bandYMin: 0.85, bandYMax: 1.0, driftPx: 24, periodMinMs: 6000, periodMaxMs: 12000 },
  leviathans: {
    shape: 'whaleSkeleton',
    planes: [
      { count: 1, widthMin: 200, widthMax: 300, speedMin: 6, speedMax: 10, alpha: 0.06, tint: 0x8fa0a8, depth: Z_LAYERS.LEVIATHAN_FAR },
    ],
    bob: { ampPx: 8, periodMinMs: 8000, periodMaxMs: 14000 },
    edgeHighlight: true,
    edgeColor: 0xa8b8c0,
    detail: 1,
    direction: 'both',
    bandYMin: 0.08,
    bandYMax: 0.3,
  },
  schoolingMotes: { count: 12, schools: 1, palette: [0x8899aa, 0x99aabb], sizeMin: 2, sizeMax: 4, speed: 12, cohesion: 0.3, alignment: 0.4, separation: 0.5, bandYMin: 0.3, bandYMax: 0.6, startle: false },
  extraLayers: [
    // Candle flames — the signature tomb element. Warm-orange flame cores + halos
    // fixed to the far-east waking slab in world pixels, with floor candles just
    // outside its edges. Irregular slow flicker (0.8–1.8 Hz) lets each breathe.
    {
      kind: 'candle',
      anchors: [
        { x: 5050, y: 1000 },
        { x: 5115, y: 920 },
        { x: 5200, y: 920 },
        { x: 5285, y: 920 },
        { x: 5350, y: 1000 },
      ],
      color: 0xffaa44,
      haloColor: 0xdd6633,
      baseAlpha: 0.85,
      flickerAmp: 0.25,
      hzMin: 0.8,
      hzMax: 1.8,
      height: 42,
      width: 12,
      haloScale: 3.2,
      wobblePx: 1.2,
    },
  ],
  // Static stone vaults: brighter distant bays, recessed middle rooms and sparse buttresses.
  parallax: [
    { svg: OPENING_AREA_BACKDROPS.tomb.far, shape: 'columns', color: 0x252735, speed: 0.02, height: 520, alpha: 0.5, detail: 0.8 },
    { svg: OPENING_AREA_BACKDROPS.tomb.mid, shape: 'columns', color: 0x191d29, speed: 0.05, height: 350, alpha: 0.73, detail: 1.0 },
    { svg: OPENING_AREA_BACKDROPS.tomb.near, shape: 'columns', color: 0x121622, speed: 0.1, height: 190, alpha: 0.72, detail: 1.0 },
  ],
  fog: { color: 0x111111, bands: 3, alphaMin: 0.08, alphaMax: 0.15, driftPx: 20, periodMinMs: 16000, periodMaxMs: 24000, yMin: 0.3, yMax: 0.7, sizeMin: 200, sizeMax: 400 },
  closer: { vignette: { color: 0x000000, edgeAlpha: 0.6, washColor: 0x0a0a12, washAlpha: 0.08 }, grade: { top: 0x223322, bottom: 0x1a1a20, strength: 0.12, blend: 'normal' } },
});
